import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { tooltips } from "../constants/tooltips";
import { giftMessages } from "../constants/giftMessages";
import { getCurrentDayOfMonth } from "../utils/currentDate";
import Player from "lottie-react";
import "../styles/App.css";

interface DayPageProps {
  setNavLoading: (v: boolean) => void;
}

const DayPage: React.FC<DayPageProps> = ({ setNavLoading }) => {
  const { day } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [mediaPath, setMediaPath] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"video" | "image" | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [giftboxAnimation, setGiftboxAnimation] = useState<any>(null);
  const [popGift, setPopGift] = useState(false);

  // Days that should use videos (edit as needed)
  const VIDEO_DAYS = [29];

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userRole = userDoc.data().role;
            setRole(userRole);

            // Check if the user is authorized to view this day
            const currentDay = getCurrentDayOfMonth();
            if (userRole === "user" && parseInt(day || "0") > currentDay) {
              setUnauthorized(true);
            }
          }
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching role:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [day, navigate]);

  // Fetch gift opened status
  useEffect(() => {
    const fetchGiftStatus = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user && day) {
          const giftStatusRef = doc(db, "users", user.uid, "giftStatus", `gift${day}`);
          const giftDoc = await getDoc(giftStatusRef);
          if (giftDoc.exists() && giftDoc.data().isOpened) {
            setIsOpened(true);
            setIsRevealed(true);
          }
        }
      } catch {}
    };
    fetchGiftStatus();
  }, [day]);

  // Fetch media (video or image)
  useEffect(() => {
    if (!day) return;
    const dayNum = parseInt(day, 10);

    if (VIDEO_DAYS.includes(dayNum)) {
      // Use video for this day
      const videoUrl = `/gifts/videos/day${day}.mp4`;
      setMediaType("video");
      setMediaPath(videoUrl);
    } else {
      // Use image for this day, try .jpg first, fallback to .JPG if needed
      const lowerUrl = `/gifts/images/day${day}.jpg`;
      setMediaType("image");
      setMediaPath(lowerUrl);
    }
  }, [day]);

  useEffect(() => {
    fetch("/gifts/images/giftbox.json")
      .then(res => res.json())
      .then(setGiftboxAnimation)
      .catch(() => setGiftboxAnimation(null));
  }, []);

  const handleBackToMain = () => {
    setNavLoading(true); // Show overlay instantly
    navigate("/main");
  };

  const handleSaveGiftStatus = async () => {
    if (!day) return;

    setSaving(true);
    setSaveMessage("Saving...");

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const giftStatusRef = doc(db, "users", user.uid, "giftStatus", `gift${day}`);
        const giftDoc = await getDoc(giftStatusRef);

        if (giftDoc.exists() && giftDoc.data().isOpened) {
          setSaveMessage("You have already saved this babe!");
        } else {
          await setDoc(giftStatusRef, { isOpened: true }, { merge: true });
          setSaveMessage("Gift status saved successfully!");
          setIsOpened(true);
        }
      } else {
        setSaveMessage("Error: User not authenticated.");
      }
    } catch (error) {
      console.error("Error saving gift status:", error);
      setSaveMessage("Error saving gift status. Please try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleGiftClick = () => {
    setPopGift(true);
    setTimeout(() => {
      setPopGift(false);
      setIsRevealed(true); // Reveal after pop animation
    }, 700); // Match the animation duration
  };

  const text = tooltips[parseInt(day || "0")] || "Content not available for this day.";
  const message = giftMessages[parseInt(day || "0")] || "Message not available for this day.";

  if (loading) {
    return <div>Loading...</div>;
  }

  if (unauthorized) {
    return (
      <div className="day-page">
        <button onClick={handleBackToMain} className="back-button">
          Back to Main Screen
        </button>
        <h1>Access Denied</h1>
        <p>You are not authorized to view this day's content.</p>
      </div>
    );
  }

  return (
    <div
      className="day-page"
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100vw",
        overflow: "hidden",
        fontFamily: "'Quicksand', 'Fredoka', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {/* Full-page gradient background */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          background: "linear-gradient(135deg, #f3e8ff 0%, #b3e5fc 100%)",
          zIndex: 0,
          pointerEvents: "none"
        }}
      />
      {/* Optional: Confetti or hearts background */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          pointerEvents: "none",
          background: "url('/hearts-bg.svg') repeat",
          opacity: 0.08,
          zIndex: 1
        }}
      />

      {/* Back button absolutely positioned */}
      <button
        onClick={handleBackToMain}
        className="back-button"
        style={{
          position: "fixed",
          top: 32,
          left: 32,
          background: "linear-gradient(90deg, #d16ba5 0%, #86a8e7 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 16,
          padding: "10px 24px",
          fontWeight: 700,
          fontFamily: "'Quicksand', 'Fredoka', sans-serif",
          fontSize: 18,
          boxShadow: "0 2px 8px #d16ba522",
          cursor: "pointer",
          zIndex: 10,
          transition: "background 0.2s, transform 0.15s"
        }}
        onMouseOver={e => (e.currentTarget.style.transform = "scale(1.07)")}
        onMouseOut={e => (e.currentTarget.style.transform = "scale(1)")}
      >
        Back to Main Screen
      </button>

      {/* Centered content box */}
      <div
        className="day-content-box"
        style={{
          background: "#fff",
          borderRadius: 32,
          boxShadow: "0 8px 32px #d16ba533, 0 2px 24px #d16ba522",
          border: "4px solid #d16ba5",
          padding: "48px 36px 36px 36px",
          textAlign: "center",
          maxWidth: 600,
          width: "90vw",
          minWidth: 320,
          margin: "80px auto 40px auto",
          minHeight: 540,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          position: "relative",
          zIndex: 2
        }}
      >
        <h1
          style={{
            color: "#d16ba5",
            fontWeight: 900,
            fontFamily: "'Fredoka', 'Quicksand', sans-serif",
            fontSize: 36,
            marginBottom: 10,
            letterSpacing: 1
          }}
        >
          Day {day}
        </h1>
        <p
          style={{
            color: "#7e57c2",
            fontSize: 20,
            fontWeight: 500,
            marginBottom: 18
          }}
        >
          {text}
        </p>
        {saveMessage && (
          <div
            className="save-message-tooltip"
            style={{
              background: "#ffe0f7",
              color: "#c2185b",
              borderRadius: 12,
              padding: "10px 18px",
              fontSize: 16,
              fontWeight: 600,
              margin: "0 auto 18px auto",
              boxShadow: "0 2px 8px #d16ba522",
              display: "inline-block"
            }}
          >
            💖 {saveMessage}
          </div>
        )}

        <div
          style={{
            minHeight: 220,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            margin: "32px auto 0 auto",
            textAlign: "center",
            position: "relative"
          }}
        >
          {/* Radial gradient spotlight behind gift */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: 260,
              height: 180,
              transform: "translate(-50%, -50%)",
              background: "radial-gradient(circle, #fff8fc 60%, transparent 100%)",
              zIndex: 0,
              pointerEvents: "none"
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            {!isRevealed ? (
              <div
                className={`gift-box-wrapper${popGift ? " gift-box-pop" : ""}`}
                style={{
                  display: "inline-block",
                  cursor: "pointer",
                  textAlign: "center",
                  marginTop: "-24px",
                  filter: "drop-shadow(0 0 24px #d16ba555)",
                  transition: "filter 0.3s"
                }}
                onClick={handleGiftClick}
              >
                {giftboxAnimation && (
                  <Player
                    autoplay
                    loop
                    animationData={giftboxAnimation}
                    style={{ height: 220, width: 220 }}
                    className="gift-box-hover"
                  />
                )}
              </div>
            ) : (
              <div className="gift-reveal" style={{ textAlign: "center" }}>
                {mediaType === "video" && mediaPath && (
                  <video controls className="day-video" style={{ maxWidth: "100%", borderRadius: 20, boxShadow: "0 4px 24px #d16ba522" }}>
                    <source src={mediaPath} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
                {mediaType === "image" && mediaPath && (
                  <>
                    <img
                      src={mediaPath}
                      alt={`Day ${day}`}
                      className="day-image day-image-pop"
                      style={{
                        width: 260,
                        height: 260,
                        objectFit: "cover",
                        borderRadius: 20,
                        boxShadow: "0 12px 40px #d16ba555, 0 4px 24px #d16ba522",
                        background: "#f3f7fa"
                      }}
                      onError={e => {
                        // Only try fallback if not already tried .JPG
                        if (mediaPath && mediaPath.endsWith('.jpg')) {
                          setMediaPath(`/gifts/images/day${day}.JPG`);
                        }
                      }}
                    />
                    <div
                      style={{
                        marginTop: 18,
                        color: "#7e57c2",
                        fontSize: 18,
                        fontWeight: 500,
                        fontFamily: "'Quicksand', 'Fredoka', sans-serif",
                        maxWidth: 340,
                        marginLeft: "auto",
                        marginRight: "auto",
                        whiteSpace: "pre-line"
                      }}
                    >
                      {message}
                    </div>
                  </>
                )}
                {!mediaType && (
                  <p style={{ color: "#d16ba5", fontWeight: 600 }}>No video or image available for this day.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleSaveGiftStatus}
          className="save-button"
          disabled={saving || isOpened}
          style={{
            marginTop: 32,
            marginBottom: 8,
            background: "linear-gradient(90deg, #d16ba5 0%, #86a8e7 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 18,
            padding: "14px 36px",
            fontWeight: 700,
            fontFamily: "'Quicksand', 'Fredoka', sans-serif",
            fontSize: 20,
            boxShadow: "0 4px 16px #d16ba522",
            cursor: saving || isOpened ? "not-allowed" : "pointer",
            opacity: saving || isOpened ? 0.6 : 1,
            transition: "background 0.2s, transform 0.15s"
          }}
          onMouseOver={e => {
            if (!(saving || isOpened)) e.currentTarget.style.transform = "scale(1.07)";
          }}
          onMouseOut={e => {
            if (!(saving || isOpened)) e.currentTarget.style.transform = "scale(1)";
          }}
        >
          I am done with this gift!
        </button>
      </div>
    </div>
  );
};

export default DayPage;